import { useMemo } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import SharedScreenAPI from "../../api/api/SharedScreenAPI";
import { pagePasswordAtom } from "./useSharedPage";
import { setSchemaAtom } from "../../atoms/GlobalAtoms";

export default function useSharedPageView(
  tenantID: string,
  sharedPageID: string,
  viewID: string,
  disabled: boolean
) {
  const pagePasswords = useAtomValue(pagePasswordAtom);
  const setSchema = useSetAtom(setSchemaAtom);
  const client = useQueryClient();

  const password = useMemo(() => {
    if (!tenantID || !sharedPageID) return null;

    return pagePasswords[`${tenantID}-${sharedPageID}`] || null;
  }, [pagePasswords, tenantID]);

  const sharedPageView = useQuery({
    queryKey: ["shared-page-view", sharedPageID, viewID],
    queryFn: () => {
      return SharedScreenAPI.getSharedPageView(
        tenantID,
        sharedPageID,
        viewID,
        password || ""
      ).then((data) => {
        if (!data.data.data.success) return data.data.data;

        for (const record of data?.data?.data?.documentRecords || []) {
          client.setQueryData(
            [tenantID, "record", "Documents", record._id],
            record
          );
        }

        for (const flow of data?.data?.data?.flowCharts || []) {
          client.setQueryData([tenantID, "record", "Flows", flow._id], flow);
        }

        setSchema(data.data.data.schema);

        return data.data.data;
      });
    },
    enabled: !!tenantID && !!sharedPageID && !!viewID && !disabled,
    staleTime: Infinity,
  });

  return {
    sharedPageView: sharedPageView.data,
    sharedPageViewLoading: sharedPageView.isLoading,
  };
}
